<template>
  <section
    class="exchange-manifest-wrapper"
    aria-label="Détail du manifeste d'échange"
    role="main"
  >
    <!-- Loading -->
    <div
      v-if="isLoading"
      class="loading-container d-print-none text-center py-5"
      aria-busy="true"
    >
      <b-spinner variant="warning" label="Chargement..." />
      <p class="text-muted mt-2">Chargement du manifeste d'échange...</p>
    </div>

    <!-- Not found -->
    <b-alert v-else-if="!manifest" variant="danger" show class="d-print-none">
      <h4 class="alert-heading">Manifeste introuvable</h4>
      <p class="alert-body mb-0">Impossible de charger ce manifeste d'échange.</p>
    </b-alert>

    <div v-else>
      <!-- Top Action Bar -->
      <div class="top-action-bar mb-4 d-print-none">
        <b-row class="align-items-center">
          <b-col cols="12" md="6">
            <h2 class="page-title mb-0">
              <feather-icon icon="RefreshCwIcon" size="24" class="mr-2 text-warning" />
              Manifeste d'échange #{{ manifest.id }}
            </h2>
            <p class="page-subtitle">Créé le {{ formatDate(manifest.created_at) }} par {{ manifest.created_by_username }}</p>
          </b-col>
          <b-col cols="12" md="6" class="text-md-right">
            <div class="action-buttons-top">
              <div class="view-mode-toggle mr-3">
                <span class="view-mode-label">Aperçu</span>
                <b-button-group size="sm">
                  <b-button
                    :variant="visualMode === 'color' ? 'warning' : 'outline-secondary'"
                    @click="visualMode = 'color'"
                  >
                    <feather-icon icon="ApertureIcon" size="14" class="mr-1" />
                    Couleur
                  </b-button>
                  <b-button
                    :variant="visualMode === 'bw' ? 'dark' : 'outline-secondary'"
                    @click="visualMode = 'bw'"
                  >
                    <feather-icon icon="PrinterIcon" size="14" class="mr-1" />
                    N&amp;B
                  </b-button>
                </b-button-group>
              </div>

              <b-dropdown
                v-ripple.400="'rgba(255,193,7,0.15)'"
                variant="outline-warning"
                right
                class="action-btn-top"
              >
                <template #button-content>
                  <feather-icon icon="PrinterIcon" size="16" class="mr-2" />
                  Imprimer
                  <feather-icon icon="ChevronDownIcon" size="14" class="ml-1" />
                </template>
                <b-dropdown-item @click="executePrint('bw')">
                  <feather-icon icon="PrinterIcon" size="16" class="mr-2" />
                  Version noir et blanc
                </b-dropdown-item>
                <b-dropdown-item @click="executePrint('color')">
                  <feather-icon icon="PrinterIcon" size="16" class="mr-2" />
                  Version couleur
                </b-dropdown-item>
              </b-dropdown>

              <b-button variant="outline-secondary" class="ml-2" :to="{ name: 'exchange-manifest-list' }">
                <feather-icon icon="ArrowLeftIcon" class="mr-1" />
                Retour
              </b-button>

              <!-- Quick Stats -->
              <div class="quick-stats d-inline-flex ml-3">
                <div class="stat-item-inline warning">
                  <feather-icon icon="PackageIcon" size="14" />
                  <span>{{ manifest.orders_count }} colis</span>
                </div>
                <div v-if="manifest.supplier" class="stat-item-inline info">
                  <feather-icon icon="UserIcon" size="14" />
                  <span>{{ manifest.supplier.username }}</span>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Printable Document -->
      <div id="manifest-content" class="manifest-content">
        <b-card :class="['manifest-card shadow-sm', manifestCardClasses]">

          <!-- Header -->
          <div class="manifest-header">
            <b-row class="align-items-center">
              <b-col cols="12" md="6" class="mb-2 mb-md-0">
                <div class="company-logo">
                  <brand-logo
                    :monochrome="visualMode === 'bw'"
                    class="logo-img"
                  />
                </div>
              </b-col>
              <b-col cols="12" md="6" class="text-md-right">
                <h1 class="manifest-title">
                  Manifeste d'échange N°
                  <span class="manifest-number">{{ manifest.id }}</span>
                </h1>
                <div class="manifest-date">
                  <feather-icon icon="CalendarIcon" size="16" class="mr-1" />
                  {{ formatDate(manifest.created_at) }}
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Company + Supplier Info -->
          <b-row class="company-info-section">
            <b-col cols="12" md="6">
              <document-company-info
                :company="company"
                header-title="Informations de la société"
                custom-tagline="Service d'échange de colis"
              />
            </b-col>
            <b-col cols="12" md="6">
              <b-card class="info-card">
                <div class="info-header">
                  <feather-icon icon="UserIcon" size="20" class="mr-2" />
                  <h5 class="mb-0">Fournisseur</h5>
                </div>
                <div class="info-content">
                  <template v-if="manifest.supplier">
                    <div class="info-item">
                      <span class="info-label">Nom:</span>
                      <span>{{ manifest.supplier.username }}</span>
                    </div>
                    <div v-if="manifest.supplier.email" class="info-item">
                      <span class="info-label">Email:</span>
                      <span>{{ manifest.supplier.email }}</span>
                    </div>
                    <div v-if="manifest.supplier.phone" class="info-item">
                      <span class="info-label">Téléphone:</span>
                      <span>{{ manifest.supplier.phone }}</span>
                    </div>
                  </template>
                  <span v-else class="text-muted">—</span>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <!-- Summary -->
          <div class="summary-section">
            <h5 class="section-title">
              <feather-icon icon="BarChartIcon" size="20" class="mr-2" />
              Résumé du manifeste
              <b-badge variant="light-warning" class="ml-2">{{ manifest.orders_count }} colis</b-badge>
            </h5>
            <div class="summary-table-container">
              <div class="table-responsive">
                <table class="modern-table">
                  <thead>
                    <tr>
                      <th>Date de création</th>
                      <th>Nombre de colis</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ formatDate(manifest.created_at) }}</td>
                      <td>{{ manifest.orders_count }}</td>
                      <td>{{ manifest.note || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Orders Table -->
          <div class="orders-section">
            <div class="section-header">
              <h5 class="section-title">
                <feather-icon icon="PackageIcon" size="20" class="mr-2" />
                Colis du manifeste
              </h5>
            </div>

            <div class="table-container">
              <b-table-lite
                responsive
                class="modern-orders-table"
                :items="orders"
                :fields="tableFields"
                striped
                hover
              >
                <template #cell(new_order_code)="data">
                  <div class="order-id-cell">
                    <strong class="order-number">#{{ data.item.new_order_code }}</strong>
                    <b-badge v-if="data.item.order_is_an_exchange" variant="warning" class="exchange-badge">Échange</b-badge>
                  </div>
                </template>

                <template #cell(source_order_code)="data">
                  <div class="order-id-cell">
                    <strong v-if="data.item.source_order_code" class="order-number">#{{ data.item.source_order_code }}</strong>
                    <span v-else>—</span>
                  </div>
                </template>

                <template #cell(address)="data">
                  <div class="address-cell">
                    <div class="address-item">
                      <feather-icon icon="MapPinIcon" size="14" class="mr-1" />
                      <strong>{{ data.item.order_governorate }}</strong>
                    </div>
                    <div v-if="data.item.order_delegation" class="address-item text-muted">
                      {{ data.item.order_delegation }}
                    </div>
                    <div class="address-item">
                      <small class="text-muted">{{ data.item.order_address }}</small>
                    </div>
                  </div>
                </template>

                <template #cell(phone)="data">
                  <div class="phone-cell">
                    <div class="phone-item">
                      <feather-icon icon="PhoneIcon" size="14" class="mr-1" />
                      {{ data.item.order_phone1 }}
                    </div>
                    <div v-if="data.item.order_phone2" class="phone-item">
                      <feather-icon icon="PhoneIcon" size="14" class="mr-1" />
                      {{ data.item.order_phone2 }}
                    </div>
                  </div>
                </template>

                <template #cell(order_name)="data">
                  <div class="customer-cell">
                    <feather-icon icon="UserIcon" size="16" class="mr-1 text-warning" />
                    {{ data.item.order_name }}
                  </div>
                </template>

              </b-table-lite>
            </div>
          </div>

          <!-- Signature -->
          <document-signature
            left-label="Signature du fournisseur"
            :company="company"
          />

        </b-card>
      </div>
    </div>
  </section>
</template>

<script>
import {
  BCard, BButton, BBadge, BSpinner, BAlert,
  BRow, BCol, BTableLite, BDropdown, BDropdownItem, BButtonGroup,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'
import { DocumentCompanyInfo, DocumentSignature } from '@/components/document'

export default {
  name: 'ExchangeManifestShow',
  directives: { Ripple },
  components: {
    BCard,
    BButton,
    BBadge,
    BSpinner,
    BAlert,
    BRow,
    BCol,
    BTableLite,
    BDropdown,
    BDropdownItem,
    BButtonGroup,
    DocumentCompanyInfo,
    DocumentSignature,
  },
  props: {
    id: { type: [String, Number], required: true },
  },
  data() {
    return {
      manifest: null,
      company: {},
      isLoading: false,
      visualMode: 'color',
      tableFields: [
        { key: 'new_order_code', label: 'Code barre' },
        { key: 'source_order_code', label: 'Colis livré' },
        { key: 'order_name', label: 'Client' },
        { key: 'phone', label: 'Téléphone' },
        { key: 'address', label: 'Adresse' },
      ],
    }
  },
  computed: {
    orders() {
      return (this.manifest?.manifest_orders || []).map(order => ({
        ...order,
        new_order_code: this.getDisplayOrderCode(
          order.order_id,
          order.order_cross_tenant_barcode,
          order.order_display_cross_tenant_barcode,
        ),
        source_order_code: this.getDisplayOrderCode(
          order.source_order_id,
          order.source_order_cross_tenant_barcode,
          order.source_order_display_cross_tenant_barcode,
        ),
      }))
    },
    manifestCardClasses() {
      return {
        'manifest-card--bw': this.visualMode === 'bw',
        'manifest-card--color': this.visualMode === 'color',
      }
    },
  },
  created() {
    this.company = storeAuth.state.company
    this.loadManifest()
  },
  methods: {
    async loadManifest() {
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/exchange-manifests/${this.id}/`)
        this.manifest = data
      } catch {
        this.manifest = null
      } finally {
        this.isLoading = false
      }
    },
    formatDate(val) {
      if (!val) return '—'
      return new Date(val).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })
    },
    getDisplayOrderCode(id, crossTenantBarcode, displayCrossTenantBarcode) {
      if (displayCrossTenantBarcode && crossTenantBarcode) {
        return crossTenantBarcode
      }
      return id || ''
    },
    buildPrintCSS() {
      return `
        <style id="print-styles">
          @page {
            size: A2 landscape;
            margin-top: 15mm;
            margin-right: 0;
            margin-bottom: 0;
            margin-left: 0;
          }
          @page :first {
            margin-top: 0;
          }
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              box-shadow: none !important;
              text-shadow: none !important;
              transition: none !important;
              animation: none !important;
            }
            html, body {
              width: 100% !important;
              height: auto !important;
              min-height: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
              color: #000 !important;
              font-family: Arial, Helvetica, sans-serif !important;
              font-size: 16px !important;
              line-height: 1.2 !important;
            }
            body * { visibility: hidden; }
            #manifest-content, #manifest-content * { visibility: visible; }
            #manifest-content {
              position: relative !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              max-width: none !important;
              margin: 0 !important;
              padding: 0 18mm !important;
              overflow: visible !important;
              box-sizing: border-box !important;
              background: #fff !important;
            }
            #app, main, section,
            .exchange-manifest-wrapper,
            .content.app-content, .app-content, .content-wrapper, .content-body {
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
              min-height: auto !important;
              overflow: visible !important;
            }
            .d-print-none, .top-action-bar, nav, .main-menu, .header-navbar-shadow,
            footer.footer, .navbar-header, .customizer-toggle, .feather-icon,
            .badge, .exchange-badge {
              display: none !important;
            }
            .manifest-card {
              width: 100% !important;
              max-width: none !important;
              margin: 0 !important;
              padding: 0 !important;
              border: 0 !important;
              border-radius: 0 !important;
              background: #fff !important;
              overflow: visible !important;
            }
            .manifest-card .card-body, .card-body {
              padding: 0 !important;
              margin: 0 !important;
              overflow: visible !important;
            }
            .manifest-header {
              margin: 0 0 10px 0 !important;
              padding: 8px 14px !important;
              border: 0 !important;
              border-bottom: 2px solid #000 !important;
              background: #fff !important;
            }
            .manifest-header .row {
              display: flex !important;
              align-items: flex-start !important;
              width: 100% !important;
              margin: 0 !important;
            }
            .manifest-header .row > [class*="col"]:first-child {
              flex: 0 0 28% !important;
              max-width: 28% !important;
              padding: 0 !important;
            }
            .manifest-header .row > [class*="col"]:last-child {
              flex: 0 0 72% !important;
              max-width: 72% !important;
              padding: 0 !important;
              text-align: right !important;
            }
            .logo-img {
              max-width: 150px !important;
              max-height: 55px !important;
              width: auto !important;
              height: auto !important;
              object-fit: contain !important;
            }
            .manifest-title {
              margin: 0 !important;
              color: #000 !important;
              font-size: 26px !important;
              line-height: 1.2 !important;
              font-weight: 700 !important;
            }
            .manifest-number {
              color: #000 !important;
            }
            .manifest-date {
              margin-top: 4px !important;
              color: #000 !important;
              font-size: 16px !important;
            }
            .row {
              display: flex !important;
              flex-wrap: wrap !important;
              margin: 0 !important;
            }
            .company-info-section {
              display: flex !important;
              gap: 14px !important;
              margin: 0 0 10px 0 !important;
              page-break-inside: avoid !important;
            }
            .company-info-section > [class*="col"] {
              display: flex !important;
              flex: 1 1 0 !important;
              max-width: none !important;
              padding: 0 !important;
            }
            .info-card, .company-card {
              width: 100% !important;
              height: auto !important;
              margin: 0 !important;
              padding: 10px !important;
              background: #fff !important;
              border: 1px solid #000 !important;
              border-radius: 0 !important;
            }
            .info-header {
              display: block !important;
              margin: 0 0 6px 0 !important;
              padding: 0 0 5px 0 !important;
              border-bottom: 1px solid #000 !important;
              color: #000 !important;
              font-size: 16px !important;
              font-weight: 700 !important;
            }
            .info-header h5,
            .company-name,
            .info-subtitle,
            .info-label,
            .info-value {
              color: #000 !important;
            }
            .company-name {
              display: block !important;
              font-size: 16px !important;
              font-weight: 700 !important;
              margin-bottom: 2px !important;
            }
            .info-subtitle {
              display: block !important;
              font-size: 13px !important;
              font-style: normal !important;
              margin-bottom: 4px !important;
            }
            .info-item {
              display: flex !important;
              gap: 8px !important;
              margin: 0 0 4px 0 !important;
              font-size: 14px !important;
              line-height: 1.2 !important;
              color: #000 !important;
            }
            .info-label {
              min-width: 30mm !important;
              font-weight: 700 !important;
              white-space: nowrap !important;
            }
            .summary-section, .orders-section {
              margin: 0 0 10px 0 !important;
            }
            .section-title {
              display: block !important;
              margin: 0 0 6px 0 !important;
              padding: 0 !important;
              border: 0 !important;
              color: #000 !important;
              font-size: 17px !important;
              font-weight: 700 !important;
            }
            .summary-table-container,
            .table-container,
            .table-responsive {
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              border: 0 !important;
              overflow: visible !important;
            }
            .modern-table,
            .modern-orders-table,
            .modern-orders-table table {
              width: 100% !important;
              border-collapse: collapse !important;
              table-layout: fixed !important;
              color: #000 !important;
            }
            .modern-table th,
            .modern-table td,
            .modern-orders-table th,
            .modern-orders-table td {
              border: 1px solid #000 !important;
              background: #fff !important;
              color: #000 !important;
              padding: 8px !important;
              vertical-align: top !important;
              white-space: normal !important;
              word-break: normal !important;
              overflow-wrap: anywhere !important;
            }
            .modern-table th,
            .modern-orders-table th {
              font-size: 15px !important;
              font-weight: 700 !important;
              text-align: left !important;
            }
            .modern-table td,
            .modern-orders-table td {
              font-size: 15px !important;
            }
            .modern-table th,
            .modern-table td {
              text-align: center !important;
            }
            .modern-orders-table th:nth-child(1),
            .modern-orders-table td:nth-child(1) { width: 16% !important; }
            .modern-orders-table th:nth-child(2),
            .modern-orders-table td:nth-child(2) { width: 16% !important; }
            .modern-orders-table th:nth-child(3),
            .modern-orders-table td:nth-child(3) { width: 20% !important; }
            .modern-orders-table th:nth-child(4),
            .modern-orders-table td:nth-child(4) { width: 16% !important; }
            .modern-orders-table th:nth-child(5),
            .modern-orders-table td:nth-child(5) { width: 32% !important; }
            .order-id-cell,
            .address-cell,
            .phone-cell,
            .customer-cell {
              display: block !important;
              color: #000 !important;
              font-size: 15px !important;
              text-align: inherit !important;
            }
            .order-number {
              color: #000 !important;
              font-weight: 700 !important;
            }
            .address-item,
            .phone-item {
              display: block !important;
              color: #000 !important;
              margin: 0 0 2px 0 !important;
            }
            .text-muted {
              color: #000 !important;
            }
            .modern-orders-table thead {
              display: table-header-group !important;
            }
            .modern-orders-table tbody tr,
            .modern-table tbody tr {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .signature-section {
              margin-top: 14px !important;
              page-break-inside: avoid !important;
            }
            .signature-section .row {
              display: flex !important;
              gap: 70px !important;
              margin: 0 !important;
            }
            .signature-section [class*="col"] {
              flex: 1 1 0 !important;
              max-width: none !important;
              padding: 0 !important;
            }
            .signature-title {
              margin: 0 0 24px 0 !important;
              color: #000 !important;
              font-size: 14px !important;
              font-weight: 700 !important;
              text-align: center !important;
            }
            .signature-line {
              height: 32px !important;
              border-bottom: 1px solid #000 !important;
              background: #fff !important;
            }
          }
        </style>
      `
    },
    executePrint(mode) {
      const previousMode = this.visualMode
      const originalTitle = document.title
      const faviconEl = document.querySelector("link[rel~='icon'], link[rel='shortcut icon']")
      const originalFaviconHref = faviconEl ? faviconEl.getAttribute('href') : null
      if (faviconEl) faviconEl.setAttribute('href', "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'></svg>")
      document.title = ''
      this.visualMode = mode
      this.$nextTick(() => {
        const printCSS = this.buildPrintCSS(mode === 'bw')
        document.head.insertAdjacentHTML('beforeend', printCSS)
        window.print()
        setTimeout(() => {
          const el = document.getElementById('print-styles')
          if (el) el.remove()
          document.title = originalTitle
          if (faviconEl && originalFaviconHref) faviconEl.setAttribute('href', originalFaviconHref)
          this.visualMode = previousMode
        }, 1000)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
$brand-warning: #e6a817;
$brand-primary: #0A2634;

.exchange-manifest-wrapper {
  padding: 1.5rem;
  background: linear-gradient(135deg, #fffdf5 0%, #fff8e1 100%);
  min-height: 100vh;
}

.top-action-bar {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-left: 4px solid $brand-warning;
}

.page-title {
  color: #1f1f1f;
  font-weight: 700;
  display: flex;
  align-items: center;
}

.page-subtitle {
  color: #718096;
  font-size: 0.875rem;
  margin: 0;
}

.action-buttons-top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-top: 1rem;
  }
}

.view-mode-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .view-mode-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #333;
  }
}

.action-btn-top {
  border-radius: 8px;
  font-weight: 600;
}

.quick-stats {
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
}

.stat-item-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;

  &.warning { background: #fff8e1; color: #7a5200; border: 1px solid #e6a817; }
  &.info    { background: #e8f4f8; color: #0A2634; border: 1px solid #9eafbb; }
}

/* ---- Printable document ---- */
.manifest-content {
  margin-top: 1rem;
}

.manifest-card {
  border-radius: 12px;
  overflow: hidden;
}

.manifest-card--color {
  background: white;
  border: 1px solid #e6c97a !important;
}

.manifest-card--bw {
  background: white;
  border: 1px solid #999 !important;
}

.manifest-header {
  padding: 1.5rem;
  background: linear-gradient(135deg, #fffbf0 0%, #fff8e1 100%);
  border-bottom: 1px solid #e6c97a;
  border-left: 5px solid $brand-warning;
  margin: -1.25rem -1.25rem 1.5rem;

  .manifest-card--bw & {
    background: white;
    border-left-color: #000;
    border-bottom-color: #000;
  }
}

.logo-img {
  max-width: 120px;
  max-height: 50px;
  object-fit: contain;
}

.manifest-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.manifest-number {
  color: $brand-warning;
  .manifest-card--bw & { color: #000; }
}

.manifest-date {
  font-size: 0.875rem;
  color: #555;
  margin-top: 0.25rem;
}

.company-info-section {
  margin-bottom: 1.5rem;
}

.info-card {
  height: 100%;
  border-left: 4px solid $brand-warning !important;

  .manifest-card--bw & { border-left-color: #000 !important; }

  .info-header {
    display: flex;
    align-items: center;
    font-weight: 700;
    font-size: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e6c97a;
    margin-bottom: 0.75rem;
    color: $brand-primary;

    .manifest-card--bw & { border-bottom-color: #000; color: #000; }
  }

  .info-item {
    display: flex;
    gap: 0.5rem;
    font-size: 0.875rem;
    margin-bottom: 0.35rem;
    align-items: baseline;

    .info-label {
      font-weight: 700;
      min-width: 80px;
      flex-shrink: 0;
    }
  }
}

.summary-section {
  margin-bottom: 1.5rem;

  .section-title {
    font-size: 1rem;
    font-weight: 700;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #e6c97a;
    color: $brand-primary;
    display: flex;
    align-items: center;
    margin-bottom: 1rem;

    .manifest-card--bw & { border-bottom-color: #000; color: #000; }
  }
}

.summary-table-container {
  border: 1px solid #e6c97a;
  border-radius: 8px;
  overflow: hidden;

  .manifest-card--bw & { border-color: #000; }
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  thead th {
    background: $brand-primary;
    color: white;
    padding: 0.75rem 1rem;
    text-align: center;
    border: 1px solid $brand-primary;

    .manifest-card--bw & { background: white; color: #000; border-color: #000; }
  }

  tbody td {
    padding: 0.75rem 1rem;
    text-align: center;
    border: 1px solid #e6c97a;
    background: white;

    .manifest-card--bw & { border-color: #000; }
  }
}

.orders-section {
  margin-bottom: 1.5rem;

  .section-title {
    font-size: 1rem;
    font-weight: 700;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #e6c97a;
    color: $brand-primary;
    display: flex;
    align-items: center;
    margin-bottom: 1rem;

    .manifest-card--bw & { border-bottom-color: #000; color: #000; }
  }
}

.modern-orders-table {
  font-size: 0.875rem;

  ::v-deep thead th {
    background: #fff8e1;
    color: #333;
    font-weight: 700;
    border: 1px solid #e6c97a;

    .manifest-card--bw & { background: white; border-color: #000; }
  }

  ::v-deep tbody td {
    border: 1px solid #f0d080;
    vertical-align: top;

    .manifest-card--bw & { border-color: #000; }
  }
}

.order-id-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.order-number {
  font-weight: 700;
  color: $brand-primary;
}

.exchange-badge {
  font-size: 0.7rem;
  align-self: flex-start;
}

.address-cell, .phone-cell {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: 0.8rem;
}

.customer-cell {
  display: flex;
  align-items: center;
  font-weight: 600;
}
</style>
